import {z} from 'zod';
import FormWithPassword from './FormWithPassword';
import FormErrorHandler from './FormErrorHandler';

class EmailPasswordForm {
	/** @type {Element} */ #form;
	#formWithPassword;
	/** @type {FormErrorHandler} */ #formErrorHandler;
	#xhrInProgress = false;
	
	/**
	 * @param {Element} form
	 * @param {Object}  params
	 */
	constructor(form, params) {
		this.#form = form;
		this.params = params || {};
		this.#formWithPassword = new FormWithPassword(this.#form);
		this.#formErrorHandler = new FormErrorHandler(this.#form);
		
		this.#initEvents();
	}
	
	#initEvents() {
		this.#form.addEventListener('submit', (ev) => {
			ev.preventDefault();
			this.submitForm();
		});
	}
	
	submitForm() {
		if (!this.isValidForm() || this.#xhrInProgress) {
			return;
		}
		
		this.#sendXhrRequest()
		    .catch((err) => {
			    this.getElement('.unknownErrorMessage').classList.remove('hidden-element');
			    console.error('#sendXhrRequest failed:', err);
		    });
	}
	
	async #sendXhrRequest() {
		this.#beforeXhrRequestSending();
		
		const result = await this.sendCustomXhrRequest();
		
		this.#handleResponse(result);
	}
	
	#beforeXhrRequestSending() {
		this.#formErrorHandler.hideFeedbackMessages();
		this.#xhrInProgress = true;
		this.getElement('.submitButton').classList.add('disabled');
	}
	
	#handleResponse({response, xhr}) {
		const responseObject = typeof xhr === 'object' ? xhr : response;
		
		responseObject
			.then(
				() => {
					this.params.onSuccess();
				}
			)
			.catch(() => {
				this.#formErrorHandler.handleErrorResponse(responseObject);
				this.onCustomXhrError(responseObject);
				this.#xhrInProgress = false;
			});
	}
	
	isCurrentEmailValid() {
		const email = this.getElement('.emailInput').value;
		
		return z.string().email().safeParse(email).success;
	}
	
	/**
	 * @returns {boolean}
	 */
	isValidForm() {
		if (!this.isCurrentEmailValid()) {
			return false;
		}
		
		return this.#formWithPassword.isPasswordValid();
	}
	
	/**
	 * @param {string} selector
	 * @returns {Element | HTMLInputElement}
	 */
	getElement(selector) {
		return this.#form.querySelector(selector);
	}
	
	/** @returns {HTMLFormElement} */
	getForm() {
		return this.#form;
	}
	
	/**
	 * @returns {FormErrorHandler}
	 * @protected
	 */
	getFormErrorHandler() {
		return this.#formErrorHandler;
	}
	
	/**
	 * @protected
	 */
	sendCustomXhrRequest() {}
	
	/**
	 * @protected
	 */
	onCustomXhrError() {}

}

export default EmailPasswordForm;